/**
 * The person's main navigation choices (`navigation.hidden` / `navigation.order`), applied the same
 * way by the web rail, the Mac rail and both Preferences lists. Web and Mac name some destinations
 * differently (the Mac's Pipeline is the web's `pipeline`, `workspace-pipeline` or `projects`), so a
 * destination carries aliases and an order written on one platform still places it on the other.
 */

/** Home is the way back from anywhere: it is always shown and always first. */
export const PINNED_NAVIGATION_IDS: readonly string[] = ['dashboard'];

/**
 * The default main navigation, the same on every platform, grouped by what people do: start, win and
 * plan the work, talk to people, then money and reference. Ids cover every platform's name for a
 * destination; rails draw a small gap where one group gives way to the next.
 */
export const DEFAULT_NAVIGATION_GROUPS: readonly (readonly string[])[] = [
  ['dashboard', 'quantis-ai'],
  ['projects', 'pipeline', 'workspace-pipeline', 'outreach', 'calendar'],
  ['messages', 'email', 'calls', 'contacts', 'employers', 'workspaces'],
  ['billing', 'accounting', 'sales', 'purchases', 'payroll', 'tax', 'assets', 'resource-library'],
];

const DEFAULT_ORDER = DEFAULT_NAVIGATION_GROUPS.flat();

/** Sorts destinations into the default order; ones it does not name keep their relative order after it. */
export function defaultNavigationOrder<T extends { id: string }>(items: readonly T[]): T[] {
  const rank = (item: T, index: number) => {
    const at = DEFAULT_ORDER.indexOf(item.id);
    return at >= 0 ? at : DEFAULT_ORDER.length + index;
  };
  return items.map((item, index) => ({ item, rank: rank(item, index) })).sort((a, b) => a.rank - b.rank).map(({ item }) => item);
}

/** The default group a destination belongs to, or undefined for one the defaults do not name. */
export function navigationGroupOf(id: string): number | undefined {
  const group = DEFAULT_NAVIGATION_GROUPS.findIndex((ids) => ids.includes(id));
  return group >= 0 ? group : undefined;
}

/**
 * Whether a rail draws a gap above this item: only in the default order, since once the person has
 * arranged their own the groups no longer describe it.
 */
export function startsNavigationGroup(ids: readonly string[], index: number, customised: boolean): boolean {
  if (customised || index === 0) return false;
  const group = navigationGroupOf(ids[index]);
  return group !== undefined && group !== navigationGroupOf(ids[index - 1]);
}

export interface NavigationEntry {
  id: string;
  /** Other ids the saved order may know this destination by. */
  aliases?: readonly string[];
}

export const isPinnedNavigationId = (id: string) => PINNED_NAVIGATION_IDS.includes(id);

const namesOf = (entry: NavigationEntry) => [entry.id, ...(entry.aliases ?? []).filter((alias) => alias !== entry.id)];

/** Sorts entries by the saved order, pinned entries first; an entry the order never mentions keeps its default place after those it does. */
export function orderNavigation<T extends NavigationEntry>(entries: readonly T[], order: readonly string[]): T[] {
  const rank = (entry: T, index: number) => {
    if (isPinnedNavigationId(entry.id)) return -1;
    const positions = namesOf(entry).map((name) => order.indexOf(name)).filter((position) => position >= 0);
    return positions.length ? Math.min(...positions) : order.length + index;
  };
  return entries
    .map((entry, index) => ({ entry, rank: rank(entry, index) }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ entry }) => entry);
}

/**
 * Saves `sequence` as the new order without losing what the order says about destinations this
 * platform does not list: their ids keep their slots, and this platform's ids fill the rest in turn.
 */
export function writeNavigationOrder(order: readonly string[], sequence: readonly NavigationEntry[]): string[] {
  const names = [...new Set(sequence.flatMap(namesOf))];
  const ours = new Set(names);
  let cursor = 0;
  const next = order.flatMap((id) => (ours.has(id) ? (cursor < names.length ? [names[cursor++]] : []) : [id]));
  return [...next, ...names.slice(cursor)];
}

/**
 * Moves one entry a step up or down past its next shown neighbour, so a move on a rail with hidden
 * entries swaps the two items the person can see. Pinned entries stay put and nothing moves above them.
 */
export function moveNavigationEntry<T extends NavigationEntry>(sequence: readonly T[], id: string, delta: -1 | 1, isShown: (entry: T) => boolean = () => true): T[] {
  const from = sequence.findIndex((entry) => entry.id === id);
  const to = neighbourIndex(sequence, from, delta, isShown);
  const next = [...sequence];
  if (to < 0 || isPinnedNavigationId(id) || isPinnedNavigationId(sequence[to].id)) return next;
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

export function canMoveNavigationEntry<T extends NavigationEntry>(sequence: readonly T[], id: string, delta: -1 | 1, isShown: (entry: T) => boolean = () => true): boolean {
  const from = sequence.findIndex((entry) => entry.id === id);
  const to = neighbourIndex(sequence, from, delta, isShown);
  return to >= 0 && !isPinnedNavigationId(id) && !isPinnedNavigationId(sequence[to].id);
}

function neighbourIndex<T>(sequence: readonly T[], from: number, delta: -1 | 1, isShown: (entry: T) => boolean): number {
  if (from < 0) return -1;
  for (let index = from + delta; index >= 0 && index < sequence.length; index += delta) {
    if (isShown(sequence[index])) return index;
  }
  return -1;
}

/** Adds or removes one id from the hidden list. Pinned ids are never hidden. */
export function setNavigationHidden(hidden: readonly string[], id: string, hide: boolean): string[] {
  const rest = hidden.filter((entry) => entry !== id);
  return hide && !isPinnedNavigationId(id) ? [...rest, id] : rest;
}
